import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

const NoInternetConnection = (props) => {
  const [isOnline, setOnline] = useState(true);
  const [isRetrying, setIsRetrying] = useState(false);
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language;

  // Simple connectivity check using navigator.onLine
  const checkConnectivity = () => {
    return navigator.onLine;
  };

  // Initial connectivity check
  useEffect(() => {
    if (typeof window !== "undefined") {
      setOnline(navigator.onLine);
    }
  }, []);

  // Listen for browser online/offline events
  useEffect(() => {
    if (typeof window !== "undefined") {
      const handleOnline = () => {
        setOnline(true);
        setIsRetrying(false);
      };

      const handleOffline = () => {
        setOnline(false);
        setIsRetrying(false);
      };

      window.addEventListener("online", handleOnline);
      window.addEventListener("offline", handleOffline);

      return () => {
        window.removeEventListener("online", handleOnline);
        window.removeEventListener("offline", handleOffline);
      };
    }
  }, []);

  const handleRetry = () => {
    setIsRetrying(true);

    // Simple retry - just check navigator.onLine
    setTimeout(() => {
      const online = checkConnectivity();
      setOnline(online);
      setIsRetrying(false);
    }, 1000);
  };

  if (isOnline) {
    return props.children;
  } else {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-200 via-white to-purple-300 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-100/20 via-transparent to-blue-100/20"></div>

        <motion.div
          transition={{ duration: 0.6, delay: 0.1 }}
          initial={{ opacity: 0, y: 30, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -30, scale: 0.95 }}
          className="relative z-10 flex flex-col items-center max-w-md mx-auto text-center"
        >
          <div className="w-full">
            {/* Animated Icon */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="mb-8"
            >
              <div className="relative mx-auto w-32 h-32 flex items-center justify-center">
                {/* Pulsing background circle */}
                <motion.div
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="absolute inset-0 bg-gradient-to-br from-purple-100 to-blue-100 rounded-full opacity-50"
                />

                {/* Main icon */}
                <motion.svg
                  width="80"
                  height="80"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="relative z-10"
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.8, delay: 0.5 }}
                >
                  <motion.path
                    initial={{ pathLength: 0, opacity: 0 }}
                    animate={{ pathLength: 1, opacity: 1 }}
                    transition={{ duration: 1.2, delay: 0.6 }}
                    d="M5.46583 2.53086C5.75902 2.23826 5.75949 1.76338 5.46689 1.4702C5.17429 1.17701 4.69941 1.17654 4.40623 1.46914C2.45721 3.41428 1.25 6.10595 1.25 9.07816C1.25 12.0852 2.48564 14.805 4.47497 16.7552C4.77076 17.0451 5.24561 17.0404 5.53557 16.7446C5.82554 16.4488 5.82082 15.974 5.52503 15.684C3.81163 14.0043 2.75 11.6659 2.75 9.07816C2.75 6.5203 3.78722 4.20612 5.46583 2.53086Z"
                    fill="#4B108D"
                  />
                  <motion.path
                    initial={{ pathLength: 0, opacity: 0 }}
                    animate={{ pathLength: 1, opacity: 1 }}
                    transition={{ duration: 1.2, delay: 0.8 }}
                    d="M19.677 1.55312C19.387 1.25733 18.9122 1.25262 18.6164 1.54258C18.3206 1.83255 18.3159 2.3074 18.6059 2.60319C20.2422 4.27243 21.25 6.55686 21.25 9.07816C21.25 11.6293 20.2182 13.9381 18.5473 15.6123C18.2547 15.9055 18.2552 16.3804 18.5484 16.673C18.8415 16.9656 19.3164 16.9651 19.609 16.6719C21.5491 14.728 22.75 12.0426 22.75 9.07816C22.75 6.14855 21.5771 3.49139 19.677 1.55312Z"
                    fill="#4B108D"
                  />
                  <motion.path
                    initial={{ pathLength: 0, opacity: 0 }}
                    animate={{ pathLength: 1, opacity: 1 }}
                    transition={{ duration: 1.2, delay: 1.0 }}
                    d="M8.27408 5.62574C8.57652 5.34272 8.59226 4.8681 8.30923 4.56566C8.02621 4.26322 7.5516 4.24748 7.24916 4.53051C6.02148 5.67937 5.25 7.28109 5.25 9.06033C5.25 10.8605 6.03967 12.4787 7.29244 13.6303C7.59739 13.9106 8.07185 13.8906 8.35216 13.5857C8.63248 13.2807 8.61251 12.8063 8.30756 12.526C7.33869 11.6354 6.75 10.4078 6.75 9.06033C6.75 7.72833 7.32517 6.51373 8.27408 5.62574Z"
                    fill="#4B108D"
                  />
                  <motion.path
                    initial={{ pathLength: 0, opacity: 0 }}
                    animate={{ pathLength: 1, opacity: 1 }}
                    transition={{ duration: 1.2, delay: 1.2 }}
                    d="M16.8033 4.58011C16.5039 4.2938 16.0292 4.30435 15.7429 4.60369C15.4566 4.90302 15.4671 5.37777 15.7664 5.66408C16.6911 6.54855 17.25 7.74727 17.25 9.06033C17.25 10.3889 16.6778 11.6006 15.7333 12.4879C15.4315 12.7716 15.4167 13.2462 15.7003 13.5481C15.9839 13.85 16.4586 13.8648 16.7604 13.5811C17.9825 12.4329 18.75 10.8349 18.75 9.06033C18.75 7.30668 18.0005 5.72524 16.8033 4.58011Z"
                    fill="#4B108D"
                  />
                  <motion.path
                    initial={{ pathLength: 0, opacity: 0 }}
                    animate={{ pathLength: 1, opacity: 1 }}
                    transition={{ duration: 1.0, delay: 1.4 }}
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M10.8326 10.6241C10.3283 10.261 10 9.66883 10 9C10 7.89543 10.8954 7 12 7C13.1046 7 14 7.89543 14 9C14 9.66883 13.6717 10.261 13.1674 10.6241L16.7147 21.7726C16.8231 22.1134 16.6759 22.4833 16.363 22.6563C16.05 22.8294 15.6584 22.7574 15.4275 22.4845L12 18.4338L8.57254 22.4845C8.34156 22.7574 7.94997 22.8294 7.63704 22.6563C7.32411 22.4833 7.17688 22.1134 7.28531 21.7726L10.8326 10.6241ZM12.9825 17.2727L14.1466 18.6485L13.5103 16.6489L12.9825 17.2727ZM12.9745 14.9648C12.9582 14.9809 12.9425 14.9978 12.9275 15.0155L12 16.1116L11.0725 15.0155C11.0575 14.9978 11.0418 14.9809 11.0255 14.9648L12 11.9022L12.9745 14.9648ZM10.4897 16.6489L11.0175 17.2727L9.85345 18.6485L10.4897 16.6489Z"
                    fill="#4B108D"
                  />
                </motion.svg>
              </div>
            </motion.div>
            {/* Title */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-2xl font-semibold text-gray-500 mb-3"
            >
              {currentLanguage === "ar"
                ? "لا يوجد اتصال بالإنترنت"
                : "No internet connection"}
            </motion.h1>
            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="text-gray-400 mb-6 leading-relaxed"
            >
              {currentLanguage === "ar"
                ? "يرجى التحقق من اتصالك بالإنترنت والمحاولة مرة أخرى"
                : "Please check your internet connection and try again"}
            </motion.p>
            {/* Retry Button */}
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              onClick={handleRetry}
              disabled={isRetrying}
              className={`
                px-6 py-3 rounded-lg font-medium transition-all duration-300
                ${
                  isRetrying
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-purple-600 hover:bg-purple-700 text-white shadow-lg hover:shadow-xl transform hover:scale-105"
                }
              `}
            >
              {isRetrying
                ? currentLanguage === "ar"
                  ? "جاري المحاولة..."
                  : "Retrying..."
                : currentLanguage === "ar"
                ? "إعادة المحاولة"
                : "Try Again"}
            </motion.button>
          </div>
        </motion.div>
      </div>
    );
  }
};

export default NoInternetConnection;
